import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Rota de dev (dev-bridge): no sandbox, uma request com `x-dev-route: <dev>`
 * é desviada pro serviço rodando no laptop desse dev, se ele estiver com
 * `abbiamo dev attach` ativo. A resposta que veio do laptop traz
 * `x-dev-bridge: <dev>`. Ver README.
 *
 * Só existe fora de prod. Em prod `installDevRoute` não registra nada: o
 * código não lê o localStorage nem manda o header.
 */
export const DEV_ROUTE_HEADER = 'x-dev-route';
export const DEV_BRIDGE_HEADER = 'x-dev-bridge';
export const DEV_ROUTE_STORAGE_KEY = 'abbiamo.devRoute';
export const DEV_ROUTE_QUERY_PARAM = 'devRoute';
export const DEV_ROUTE_MAX_LENGTH = 32;

// Mesmo formato do backend (`normalizeDevRoute` da nest-utils), com teto menor.
const DEV_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const NON_PROD_ENVS = new Set(['LOCAL', 'DEV', 'SANDBOX']);
// Trava extra: vários blocos de branch do netlify.toml apontam o preview pra
// API de prod. Se algum deles vier com NX_ENV errado, o host ainda barra.
const PROD_API_HOSTS = new Set(['dashboard-api.abbiamo.io']);

type Env = Record<string, string | undefined>;

export const isDevRouteAvailable = (env: Env = process.env as Env): boolean => {
  if (!NON_PROD_ENVS.has(env['NX_ENV'] ?? '')) return false;
  try {
    const host = new URL(env['NX_API_URL'] ?? '').hostname.toLowerCase();
    return !PROD_API_HOSTS.has(host);
  } catch {
    return false;
  }
};

/** Nome normalizado (trim + minúsculas) se for válido; senão `null`. */
export const normalizeDevRouteName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const name = value.trim().toLowerCase();
  if (!name || name.length > DEV_ROUTE_MAX_LENGTH) return null;
  return DEV_NAME_RE.test(name) ? name : null;
};

export type DevRouteLastResponse =
  | { kind: 'laptop'; dev: string; at: number }
  | { kind: 'sandbox'; at: number }
  | { kind: 'blocked'; at: number };

export interface DevRouteSnapshot {
  available: boolean;
  route: string | null;
  last: DevRouteLastResponse | null;
}

const readStorage = (): string | null => {
  try {
    return normalizeDevRouteName(
      window.localStorage.getItem(DEV_ROUTE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
};

let snapshot: DevRouteSnapshot = { available: false, route: null, last: null };
const listeners = new Set<() => void>();

const update = (patch: Partial<DevRouteSnapshot>) => {
  snapshot = { ...snapshot, ...patch };
  listeners.forEach((l) => l());
};

export const subscribeDevRoute = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getDevRouteSnapshot = (): DevRouteSnapshot => snapshot;

/** Rota ativa, lida na hora. Fora de prod só; em prod é sempre `null`. */
export const getDevRoute = (): string | null => {
  if (!isDevRouteAvailable()) return null;
  return readStorage();
};

/**
 * Liga (`name`) ou desliga (`null`/vazio). Devolve `false` se o nome for
 * inválido ou se o ambiente for prod.
 */
export const setDevRoute = (name: string | null): boolean => {
  if (!isDevRouteAvailable()) return false;
  const clear = name === null || name.trim() === '';
  const normalized = clear ? null : normalizeDevRouteName(name);
  if (!clear && !normalized) return false;
  try {
    if (normalized) {
      window.localStorage.setItem(DEV_ROUTE_STORAGE_KEY, normalized);
    } else {
      window.localStorage.removeItem(DEV_ROUTE_STORAGE_KEY);
    }
  } catch {
    // localStorage bloqueado: a rota não persiste, então não liga.
    return false;
  }
  update({ route: normalized, last: null });
  return true;
};

/** `?devRoute=danilo` liga, `?devRoute=` desliga, ausente não mexe. */
export const applyDevRouteFromSearch = (search: string): void => {
  const params = new URLSearchParams(search);
  if (!params.has(DEV_ROUTE_QUERY_PARAM)) return;
  const value = params.get(DEV_ROUTE_QUERY_PARAM) ?? '';
  if (!setDevRoute(value)) {
    console.warn(
      `[dev-route] nome inválido em ?${DEV_ROUTE_QUERY_PARAM}=${value} (use [a-z0-9-], até ${DEV_ROUTE_MAX_LENGTH} caracteres)`,
    );
  }
};

export const devRouteRequestInterceptor = (
  config: AxiosRequestConfig,
): AxiosRequestConfig => {
  const route = getDevRoute();
  if (!route) return config;
  config.headers
    ? (config.headers[DEV_ROUTE_HEADER] = route)
    : (config.headers = { [DEV_ROUTE_HEADER]: route });
  return config;
};

const recordResponse = (response?: AxiosResponse) => {
  if (!snapshot.route) return;
  const at = Date.now();
  if (!response) {
    // Sem resposta nenhuma: rede ou preflight do CORS barrando o header.
    update({ last: { kind: 'blocked', at } });
    return;
  }
  const dev = response.headers?.[DEV_BRIDGE_HEADER];
  update({
    last: dev
      ? { kind: 'laptop', dev: String(dev), at }
      : { kind: 'sandbox', at },
  });
};

let storageListenerInstalled = false;

/**
 * Registra o header de rota dev num cliente axios que fala com o backend da
 * Abbiamo. Nunca usar em cliente de terceiros (Cognito, Supabase, PostHog):
 * o preflight deles não aceita o header. Em prod não faz nada.
 */
export const installDevRoute = (instance: AxiosInstance): void => {
  if (!isDevRouteAvailable()) return;

  if (!snapshot.available) {
    update({ available: true, route: readStorage(), last: null });
    if (typeof window !== 'undefined') {
      applyDevRouteFromSearch(window.location.search);
      if (!storageListenerInstalled) {
        storageListenerInstalled = true;
        window.addEventListener('storage', (e) => {
          if (e.key === DEV_ROUTE_STORAGE_KEY) {
            update({ route: readStorage(), last: null });
          }
        });
      }
    }
  }

  instance.interceptors.request.use(devRouteRequestInterceptor);
  instance.interceptors.response.use(
    (response) => {
      recordResponse(response);
      return response;
    },
    (error) => {
      recordResponse(error?.response);
      return Promise.reject(error);
    },
  );
};

/** Só pra teste. */
export const __resetDevRouteForTests = () => {
  snapshot = { available: false, route: null, last: null };
  listeners.clear();
};
